import { clsx } from 'clsx'
import { forwardRef, useCallback, useRef, useState, useMemo } from 'react'
import type React from 'react'
import { useParams } from 'react-router'

import { AffirmationType, AFFIRMATION_TYPES } from './AffirmationData'
import CardFace from './CardFace'
import Overlay from './Overlay'
import { useCardShare } from './useCardShare'
import useRandomAffirmationsData from './useRandomAffirmationsData'
import Wrapper from './Wrapper'

type CardRect = { top: number; left: number; width: number; height: number }

const NUMBER_OF_AFFIRMATIONS = 6

export default function AffirmationsChoice() {
  const types = useType()
  const { affirmations, refresh } = useRandomAffirmationsData(NUMBER_OF_AFFIRMATIONS, types)

  const [activeCard, setActiveCard] = useState<number | null>(null)
  const [activeRect, setActiveRect] = useState<CardRect | null>(null)
  const [isClosing, setIsClosing] = useState(false)
  const cardRefs = useRef<(HTMLDivElement | null)[]>([])
  const cardFaceRef = useRef<HTMLDivElement>(null)
  const activeCardText =
    activeCard !== null && affirmations ? (affirmations[activeCard]?.text ?? null) : null
  const { handleShare, handleDownload, handleCopy } = useCardShare(cardFaceRef, activeCardText)

  const handleCardClick = useCallback((id: number) => {
    const el = cardRefs.current[id]
    if (el) {
      const { top, left, width, height } = el.getBoundingClientRect()
      setActiveRect({ top, left, width, height })
    }
    setActiveCard(id)
    setIsClosing(false)
  }, [])

  /** Triggers close animation; actual removal happens in handleDepartEnd */
  const handleClose = useCallback(() => {
    setIsClosing(true)
    refresh()
  }, [refresh])

  /** Called when the depart animation finishes — cleans up state */
  const handleDepartEnd = useCallback(() => {
    setActiveCard(null)
    setActiveRect(null)
    setIsClosing(false)
  }, [])

  return (
    <Wrapper>
      <div
        className={clsx(
          'choice-container flex min-h-0 w-full max-w-none min-w-0 shrink grow flex-col items-center',
          'sm:gap-6 md:gap-8 lg:gap-10',
        )}
      >
        <div className="flex flex-col items-center gap-3 text-center md:gap-3.5 xl:gap-4">
          <h1 className="choice-container-title text-4xl font-medium tracking-wide">
            Trust your intuition
          </h1>
          <h2 className="choice-container-title text-2xl font-normal italic">
            Let go. Just feel… ask your question and choose your card
          </h2>
        </div>
        <div className="cards-container min-h-0 w-full flex-1 content-center">
          <div
            className={clsx(
              'choice-grid min-h-0 w-full flex-1 content-center',
              activeCard !== null && 'choice-grid--focused',
            )}
          >
            {Array(NUMBER_OF_AFFIRMATIONS)
              .fill(null)
              .map((_, index) => (
                <Card3D
                  ref={(el) => {
                    cardRefs.current[index] = el
                  }}
                  key={index}
                  isActive={activeCard === index}
                  isClosing={isClosing}
                  onClick={() => handleCardClick(index)}
                />
              ))}
          </div>
        </div>
        {activeCard !== null && activeRect !== null && (
          <>
            <Overlay
              isClosing={isClosing}
              onClick={handleClose}
              onShare={handleShare}
              onDownload={handleDownload}
              onCopy={handleCopy}
            />
            <ActiveCard
              fromRect={activeRect}
              text={activeCardText || ''}
              isClosing={isClosing}
              cardFaceRef={cardFaceRef}
              onDepartEnd={handleDepartEnd}
            />
          </>
        )}
      </div>
    </Wrapper>
  )
}

type Card3DProps = {
  isActive: boolean
  isClosing: boolean
  onClick: () => void
}

const Card3D = forwardRef<HTMLDivElement, Card3DProps>(function Card3D(
  { isActive, isClosing, onClick },
  ref,
) {
  const isGhost = isActive && !isClosing
  const isReappearing = isActive && isClosing
  return (
    <div
      ref={ref}
      className={clsx('choice-card card-shadow-hover hover-3d cursor-pointer transition-all', {
        invisible: isGhost,
        'choice-card--reappear': isReappearing,
      })}
      onClick={onClick}
    >
      <CardBack />
      {/* DaisyUI hover-3d needs 8 extra empty divs */}
      <div aria-hidden="true"></div>
      <div aria-hidden="true"></div>
      <div aria-hidden="true"></div>
      <div aria-hidden="true"></div>
      <div aria-hidden="true"></div>
      <div aria-hidden="true"></div>
      <div aria-hidden="true"></div>
      <div aria-hidden="true"></div>
    </div>
  )
})

type ActiveCardProps = {
  fromRect: CardRect
  text: string
  isClosing: boolean
  onDepartEnd: () => void
  cardFaceRef: React.RefObject<HTMLDivElement | null>
}

function ActiveCard({ fromRect, text, isClosing, onDepartEnd, cardFaceRef }: ActiveCardProps) {
  function handleAnimationEnd(e: React.AnimationEvent) {
    if (e.animationName === 'choice-card-depart') {
      onDepartEnd()
    }
  }

  return (
    <div
      className={clsx('choice-card-active z-20', isClosing && 'choice-card-active--closing')}
      style={
        {
          '--from-top': `${fromRect.top}px`,
          '--from-left': `${fromRect.left}px`,
          '--from-width': `${fromRect.width}px`,
          '--from-height': `${fromRect.height}px`,
        } as React.CSSProperties
      }
      onAnimationEnd={handleAnimationEnd}
    >
      <div
        className={clsx(
          'choice-card-active-inner',
          isClosing && 'choice-card-active-inner--closing',
        )}
      >
        <div className="choice-card-face choice-card-face--back">
          <CardBack />
        </div>
        <CardFace title={text} ref={cardFaceRef} />
      </div>
    </div>
  )
}

function CardBack() {
  return <div className={clsx('card choice-card-back h-full w-full')} />
}

const DEFAULT_TYPE: AffirmationType = AFFIRMATION_TYPES.DAILY

function useType(): AffirmationType[] {
  const { type: typeParam } = useParams()

  let type = DEFAULT_TYPE

  if (typeParam && Object.values(AFFIRMATION_TYPES).includes(typeParam as AffirmationType)) {
    type = typeParam as AffirmationType
  }

  const types = useMemo(() => {
    if (type === AFFIRMATION_TYPES.DAILY) {
      return [AFFIRMATION_TYPES.DAILY, AFFIRMATION_TYPES.SELF_LOVE, AFFIRMATION_TYPES.ANXIETY]
    }
    return [type]
  }, [type])

  return types
}
